// i18n
import { TFunction } from 'i18next';
import { Trans } from 'react-i18next';

// types
import { Account, Activity, Entity } from '../@types';

// router
import { Link as RouterLink } from 'react-router-dom';

// mui
import { Link, Typography } from '@mui/material';

// links
import { LinkAccount } from '../components/links/LinkAccount/LinkAccount';
import { LinkDocument } from '../components/links/LinkDocument/LinkDocument';
import { LinkEntity } from '../components/links/LinkEntity/LinkEntity';

// ui
import {
	entityIconSubtypes,
	entityIconTypes,
	financierIconTypes,
	IconCircleProps,
} from '../components/ui/IconCircle/IconCircle';
import {
	ilapCategoryIconTypes,
	transactionIconTypes,
} from '../components/ui/IconCircle/IconCircle.utils';
import { LineItemProps } from '../components/ui/LineItem/LineItem';

// utils
import { formatCurrency } from './currency';
import { toCamelCase, truncate } from './strings';
import { translateActivityType, translateFormName, translateType } from './translations';

export const populateActivities = [
	'account',
	'actor',
	'benefit',
	'category',
	'comment',
	'component',
	'contract',
	'document',
	'documentType',
	'entity',
	'expenseType',
	'form',
	'fromAccount',
	'ifr',
	'ifrPlan',
	'ifrPlanVersion',
	'ilapJob',
	'loan',
	'program',
	'project',
	'reconciliationItem',
	'toAccount',
	'transaction',
	'transaction.fromAccount',
	'transaction.fromEntity',
	'transaction.toAccount',
	'user',
];

export const paramPopulateActivities = populateActivities.join(',');

// V2 lifecycle activity types — these don't carry a populated entity object
// on the activity row, so they fall through the main switch. Map them
// directly to the matching `activities.v2*` translation key already defined
// in the locale file.
const activityTypeToV2I18nKey: Record<string, string> = {
	CONDITION_CREATED: 'activities.v2ConditionCreated',
	CONDITION_DELETED: 'activities.v2ConditionDeleted',
	CONDITION_UPDATED: 'activities.v2ConditionUpdated',
	CONTRACT_APPROVED: 'activities.v2ContractApproved',
	CONTRACT_CANCELLED: 'activities.v2ContractCancelled',
	CONTRACT_COMPLETED: 'activities.v2ContractCompleted',
	CONTRACT_DELETED: 'activities.v2ContractDeleted',
	CONTRACT_REOPENED: 'activities.v2ContractReopened',
	CONTRACT_RETURNED: 'activities.v2ContractReturned',
	CONTRACT_SUBMITTED: 'activities.v2ContractSubmitted',
	EXCEPTION_CREATED: 'activities.v2ExceptionCreated',
	EXCEPTION_DELETED: 'activities.v2ExceptionDeleted',
	EXCEPTION_UPDATED: 'activities.v2ExceptionUpdated',
	INVOICE_AI_EXTRACTED: 'activities.v2InvoiceAiExtracted',
	INVOICE_APPROVED: 'activities.v2InvoiceApproved',
	INVOICE_CREATED: 'activities.v2InvoiceCreated',
	INVOICE_DOCUMENT_UPLOADED: 'activities.v2InvoiceDocumentUploaded',
	INVOICE_PAID: 'activities.v2InvoicePaid',
	INVOICE_PAYMENT_STARTED: 'activities.v2InvoicePaymentStarted',
	INVOICE_PUT_ON_HOLD: 'activities.v2InvoicePutOnHold',
	INVOICE_REJECTED: 'activities.v2InvoiceRejected',
	INVOICE_REOPENED: 'activities.v2InvoiceReopened',
	INVOICE_RESUMED: 'activities.v2InvoiceResumed',
	INVOICE_SUBMITTED: 'activities.v2InvoiceSubmitted',
	INVOICE_UPDATED: 'activities.v2InvoiceUpdated',
	PLANNING_ACTIVITY_CREATED: 'activities.v2ActivityCreated',
	PLANNING_ACTIVITY_DELETED: 'activities.v2ActivityDeleted',
	PLANNING_ACTIVITY_UPDATED: 'activities.v2ActivityUpdated',
	SPEND_PLAN_APPROVED: 'activities.v2SpendPlanApproved',
	SPEND_PLAN_COMPLETED: 'activities.v2SpendPlanCompleted',
	SPEND_PLAN_CREATED: 'activities.v2SpendPlanCreated',
	SPEND_PLAN_DELETED: 'activities.v2SpendPlanDeleted',
	SPEND_PLAN_REJECTED: 'activities.v2SpendPlanRejected',
	SPEND_PLAN_REOPENED: 'activities.v2SpendPlanReopened',
	SPEND_PLAN_RETURNED: 'activities.v2SpendPlanReturned',
	SPEND_PLAN_SUBMITTED: 'activities.v2SpendPlanSubmitted',
	SPEND_PLAN_UPDATED: 'activities.v2SpendPlanUpdated',
	VOUCHER_APPROVED: 'activities.v2VoucherApproved',
	VOUCHER_CANCELLED: 'activities.v2VoucherCancelled',
	VOUCHER_CREATED: 'activities.v2VoucherCreated',
	VOUCHER_DELETED: 'activities.v2VoucherDeleted',
	VOUCHER_REDEEMED: 'activities.v2VoucherRedeemed',
	VOUCHER_REJECTED: 'activities.v2VoucherRejected',
	VOUCHER_UPDATED: 'activities.v2VoucherUpdated',
	VOUCHER_VERIFIED: 'activities.v2VoucherVerified',
	WORK_PLAN_APPROVED: 'activities.v2WorkPlanApproved',
	WORK_PLAN_COMPLETED: 'activities.v2WorkPlanCompleted',
	WORK_PLAN_CREATED: 'activities.v2WorkPlanCreated',
	WORK_PLAN_DELETED: 'activities.v2WorkPlanDeleted',
	WORK_PLAN_LOCKED: 'activities.v2WorkPlanLocked',
	WORK_PLAN_REJECTED: 'activities.v2WorkPlanRejected',
	WORK_PLAN_REOPENED: 'activities.v2WorkPlanReopened',
	WORK_PLAN_RETURNED: 'activities.v2WorkPlanReturned',
	WORK_PLAN_SUBMITTED: 'activities.v2WorkPlanSubmitted',
	WORK_PLAN_UPDATED: 'activities.v2WorkPlanUpdated',
	// One-way soft-delete (DEACTIVATED) lifecycle types.
	CONDITION_DEACTIVATED: 'activities.v2ConditionDeactivated',
	CONTRACT_DEACTIVATED: 'activities.v2ContractDeactivated',
	CUSTOM_FIELD_DEACTIVATED: 'activities.v2CustomFieldDeactivated',
	EXCEPTION_DEACTIVATED: 'activities.v2ExceptionDeactivated',
	INVOICE_DEACTIVATED: 'activities.v2InvoiceDeactivated',
	PLANNING_ACTIVITY_DEACTIVATED: 'activities.v2ActivityDeactivated',
	PROGRESS_ENTRY_DEACTIVATED: 'activities.v2ProgressEntryDeactivated',
	SPEND_PLAN_ALLOCATION_DEACTIVATED: 'activities.v2SpendPlanAllocationDeactivated',
	SPEND_PLAN_DEACTIVATED: 'activities.v2SpendPlanDeactivated',
	SPREADSHEET_DEACTIVATED: 'activities.v2SpreadsheetDeactivated',
	UPLOAD_TEMPLATE_DEACTIVATED: 'activities.v2UploadTemplateDeactivated',
	VERIFICATION_GROUP_DEACTIVATED: 'activities.v2VerificationGroupDeactivated',
	VOUCHER_DEACTIVATED: 'activities.v2VoucherDeactivated',
	WORK_PLAN_DEACTIVATED: 'activities.v2WorkPlanDeactivated',
};

export interface ConstructActivityItemsProps {
	activities: Activity[];
	t: TFunction<'translation', undefined>;
}

export const constructActivityItems = ({
	activities,
	t,
}: ConstructActivityItemsProps): LineItemProps[] => {
	return activities.map((activity) => {
		let amount;
		let components;
		let linkEntity = (
			<Link
				component={RouterLink}
				fontWeight={600}
				to={`/project/${activity.project?.idWbg}/entities`}
				underline="hover"
			/>
		);
		let fromAccount;
		let fromEntity;
		let i18nKey;
		let title;
		let to: string | undefined;
		let toAccount;
		let toEntity;
		let type: IconCircleProps['type'];
		let values;

		if (activity.account) {
			to = `/project/${activity.project?.idWbg}/account/${activity.account.id}`;
		} else if (activity.entity) {
			linkEntity = <LinkEntity project={activity.project} entity={activity.entity} />;
			to = `/project/${activity.project?.idWbg}/entity/${activity.entity.id}`;
			type =
				activity.entity.type === 'BENEFICIARY'
					? entityIconSubtypes[activity.entity.subtype]
					: entityIconTypes[activity.entity.type];
		} else if (activity.loan) {
			to = `/project/${activity.project?.idWbg}/financing`;
		}

		if (activity.transaction) {
			amount = formatCurrency({
				currency: activity.transaction.currency,
				notation: 'compact',
				value: activity.transaction.amount + (activity.transaction.tax || 0),
			});
			fromAccount = (activity.fromAccount || activity.transaction.fromAccount) as Account;
			fromEntity = activity.transaction.fromEntity as Entity;
			toAccount = (activity.toAccount || activity.transaction.toAccount) as Account;
			toEntity = activity.transaction.toEntity as Entity;
		}

		switch (activity.type) {
			case 'ACCOUNT_CREATED':
				if (activity.account) {
					components = {
						anchor: <LinkAccount account={activity.account} project={activity.project} />,
					};
					i18nKey = 'activities.accountCreated';
					type = activity.account.type === 'LOCAL' ? 'local' : 'account';
					values = { name: activity.account.name };
				}
				break;

			case 'ACCOUNT_UPDATED':
				if (activity.account) {
					components = {
						anchor: <LinkAccount account={activity.account} project={activity.project} />,
					};
					i18nKey = 'activities.accountUpdated';
					type = activity.account.type === 'LOCAL' ? 'local' : 'account';
					values = { name: activity.account.name };
				}
				break;

			case 'ACT_CREATED':
				if (activity.act) {
					components = {
						anchor: (
							<Link component={RouterLink} to={`/project/${activity.project?.idWbg}/activities`} />
						),
					};
					i18nKey = 'activities.actCreated';
					type = 'act';
					values = { name: activity.act.name };
				}
				break;

			case 'ACT_UPDATED':
				if (activity.act) {
					components = {
						anchor: (
							<Link component={RouterLink} to={`/project/${activity.project?.idWbg}/activities`} />
						),
					};
					i18nKey = 'activities.actUpdated';
					type = 'act';
					values = { name: activity.act.name };
				}
				break;

			case 'BENEFIT_CREATED':
				if (activity.benefit) {
					components = {
						anchor: (
							<Link
								component={RouterLink}
								to={`/project/${activity.project?.idWbg}/settings/benefits`}
							/>
						),
					};
					i18nKey = 'activities.benefitCreated';
					type = 'nonCashBenefit';
					values = { name: activity.benefit.name };
				}
				break;

			case 'BENEFIT_UPDATED':
				if (activity.benefit) {
					components = {
						anchor: (
							<Link
								component={RouterLink}
								to={`/project/${activity.project?.idWbg}/settings/benefits`}
							/>
						),
					};
					i18nKey = 'activities.benefitUpdated';
					type = 'nonCashBenefit';
					values = { name: activity.benefit.name };
				}
				break;

			case 'CATEGORY_ACTIVATED':
				if (activity.category) {
					components = {
						anchor: (
							<Link
								component={RouterLink}
								to={`/project/${activity.project?.idWbg}/settings/categories`}
							/>
						),
					};
					i18nKey = 'activities.categoryActivated';
					type = 'category';
					values = { name: activity.category.name };
				}
				break;

			case 'CATEGORY_CREATED':
				if (activity.category) {
					components = {
						anchor: (
							<Link
								component={RouterLink}
								to={`/project/${activity.project?.idWbg}/settings/categories`}
							/>
						),
					};
					i18nKey = 'activities.categoryCreated';
					type = 'category';
					values = { name: activity.category.name };
				}
				break;

			case 'CATEGORY_DEACTIVATED':
				if (activity.category) {
					components = {
						anchor: (
							<Link
								component={RouterLink}
								to={`/project/${activity.project?.idWbg}/settings/categories`}
							/>
						),
					};
					i18nKey = 'activities.categoryDeactivated';
					type = 'category';
					values = { name: activity.category.name };
				}
				break;

			case 'CATEGORY_UPDATED':
				if (activity.category) {
					components = {
						anchor: (
							<Link
								component={RouterLink}
								to={`/project/${activity.project?.idWbg}/settings/categories`}
							/>
						),
					};
					i18nKey = 'activities.categoryUpdated';
					type = 'category';
					values = { name: activity.category.name };
				}
				break;

			case 'COMMENT_CREATED':
				if (activity.comment) {
					components = {
						anchor: (
							<Link
								component={RouterLink}
								to={`/project/${activity.project?.idWbg}/transactions`}
							/>
						),
					};
					i18nKey = 'activities.commentCreated';
					type = 'comment';
				}
				break;

			case 'COMPONENT_ACTIVATED':
				if (activity.component) {
					components = {
						anchor: (
							<Link
								component={RouterLink}
								to={`/project/${activity.project?.idWbg}/settings/components`}
							/>
						),
					};
					i18nKey = 'activities.componentActivated';
					type = 'component';
					values = { name: activity.component.name };
				}
				break;

			case 'COMPONENT_CREATED':
				if (activity.component) {
					components = {
						anchor: (
							<Link
								component={RouterLink}
								to={`/project/${activity.project?.idWbg}/settings/components`}
							/>
						),
					};
					i18nKey = 'activities.componentCreated';
					type = 'component';
					values = { name: activity.component.name };
				}
				break;

			case 'COMPONENT_DEACTIVATED':
				if (activity.component) {
					components = {
						anchor: (
							<Link
								component={RouterLink}
								to={`/project/${activity.project?.idWbg}/settings/components`}
							/>
						),
					};
					i18nKey = 'activities.componentDeactivated';
					type = 'component';
					values = { name: activity.component.name };
				}
				break;

			case 'COMPONENT_UPDATED':
				if (activity.component) {
					components = {
						anchor: (
							<Link
								component={RouterLink}
								to={`/project/${activity.project?.idWbg}/settings/components`}
							/>
						),
					};
					i18nKey = 'activities.componentUpdated';
					type = 'component';
					values = { name: activity.component.name };
				}
				break;

			case 'CONTRACT_CREATED':
				if (activity.contract) {
					components = {
						anchor: (
							<Link
								component={RouterLink}
								fontWeight={600}
								to={`/project/${activity.project?.idWbg}/contracts`}
								underline="hover"
							/>
						),
					};
					i18nKey = 'activities.contractCreated';
					type = 'contract';
					values = { num: activity.contract.num };
				}
				break;

			case 'CONTRACT_UPDATED':
				if (activity.contract) {
					components = {
						anchor: (
							<Link
								component={RouterLink}
								fontWeight={600}
								to={`/project/${activity.project?.idWbg}/contracts`}
								underline="hover"
							/>
						),
					};
					i18nKey = 'activities.contractUpdated';
					type = 'contract';
					values = { num: activity.contract.num };
				}
				break;

			case 'DOCUMENT_CREATED':
				if (activity.document) {
					components = {
						anchor: <LinkDocument document={activity.document} />,
					};
					i18nKey = 'activities.documentCreated';
					type = 'document';
					values = { name: `${truncate({ end: 20, str: activity.document.name })}...` };
				}
				break;

			case 'DOCUMENT_UPDATED':
				if (activity.document) {
					components = {
						anchor: <LinkDocument document={activity.document} />,
					};
					i18nKey = 'activities.documentUpdated';
					type = 'document';
					values = { name: `${truncate({ end: 20, str: activity.document.name })}...` };
				}
				break;

			case 'DOCUMENT_TYPE_CREATED':
				if (activity.documentType) {
					components = {
						strong: <strong />,
					};
					i18nKey = 'activities.documentTypeCreated';
					type = 'documentType';
					values = { name: activity.documentType.name };
				}
				break;

			case 'DOCUMENT_TYPE_UPDATED':
				if (activity.documentType) {
					components = {
						strong: <strong />,
					};
					i18nKey = 'activities.documentTypeUpdated';
					type = 'documentType';
					values = { name: activity.documentType.name };
				}
				break;

			case 'ENTITY_CREATED':
				if (activity.entity) {
					components = {
						anchor: linkEntity,
					};
					i18nKey = 'activities.entityCreated';
					values = {
						name: activity.entity.name,
						type: translateType(activity.entity.type, t),
					};
				}
				break;

			case 'ENTITY_UPDATED':
				if (activity.entity) {
					components = {
						anchor: linkEntity,
					};
					i18nKey = 'activities.entityUpdated';
					values = {
						name: activity.entity.name,
						type: translateType(activity.entity.type, t),
					};
				}
				break;

			case 'ENTITY_WALLET_ACTIVATED':
				if (activity.entity) {
					components = {
						anchor: linkEntity,
					};
					i18nKey = 'activities.entityWalletActivated';
					values = {
						name: activity.entity.name,
						type: translateType(activity.entity.type, t),
					};
				}
				break;

			case 'ENTITY_WALLET_DEACTIVATED':
				if (activity.entity) {
					components = {
						anchor: linkEntity,
					};
					i18nKey = 'activities.entityWalletDeactivated';
					values = {
						name: activity.entity.name,
						type: translateType(activity.entity.type, t),
					};
				}
				break;

			case 'EXPENSE_TYPE_ACTIVATED':
				if (activity.expenseType) {
					if (activity.project) {
						components = {
							anchor: (
								<Link
									component={RouterLink}
									to={`/project/${activity.project?.idWbg}/settings/expense-types`}
								/>
							),
						};
						i18nKey = 'activities.expenseTypeWithProjectActivated';
					} else {
						components = {
							strong: <strong />,
						};
						i18nKey = 'activities.expenseTypeActivated';
					}
					type = 'expenseType';
					values = { name: activity.expenseType.name };
				}
				break;

			case 'EXPENSE_TYPE_CREATED':
				if (activity.expenseType) {
					if (activity.project) {
						components = {
							anchor: (
								<Link
									component={RouterLink}
									to={`/project/${activity.project?.idWbg}/settings/expense-types`}
								/>
							),
						};
						i18nKey = 'activities.expenseTypeWithProjectCreated';
					} else {
						components = {
							strong: <strong />,
						};
						i18nKey = 'activities.expenseTypeCreated';
					}
					type = 'expenseType';
					values = { name: activity.expenseType.name };
				}
				break;

			case 'EXPENSE_TYPE_DEACTIVATED':
				if (activity.expenseType) {
					if (activity.project) {
						components = {
							anchor: (
								<Link
									component={RouterLink}
									to={`/project/${activity.project?.idWbg}/settings/expense-types`}
								/>
							),
						};
						i18nKey = 'activities.expenseTypeWithProjectDeactivated';
					} else {
						components = {
							strong: <strong />,
						};
						i18nKey = 'activities.expenseTypeDeactivated';
					}
					type = 'expenseType';
					values = { name: activity.expenseType.name };
				}
				break;

			case 'EXPENSE_TYPE_UPDATED':
				if (activity.expenseType) {
					if (activity.project) {
						components = {
							anchor: (
								<Link
									component={RouterLink}
									to={`/project/${activity.project?.idWbg}/settings/expense-types`}
								/>
							),
						};
						i18nKey = 'activities.expenseTypeWithProjectUpdated';
					} else {
						components = {
							strong: <strong />,
						};
						i18nKey = 'activities.expenseTypeUpdated';
					}
					type = 'expenseType';
					values = { name: activity.expenseType.name };
				}
				break;

			case 'FORM_CREATED':
				if (activity.form) {
					components = {
						strong: <strong />,
					};
					i18nKey = 'activities.formCreated';
					type = 'form';
					values = { name: translateFormName(activity.form.name, t) };
				}
				break;

			case 'FORM_UPDATED':
				if (activity.form) {
					components = {
						strong: <strong />,
					};
					i18nKey = 'activities.formUpdated';
					type = 'form';
					values = { name: translateFormName(activity.form.name, t) };
				}
				break;

			case 'IFR_CREATED':
				if (activity.ifr) {
					components = {
						anchor: (
							<Link
								component={RouterLink}
								to={`/project/${activity.project?.idWbg}/ifr/${activity.ifr.id}`}
							/>
						),
					};
					i18nKey = 'activities.ifrCreated';
					type = 'ifr';
					values = { name: activity.ifr.name };
				}
				break;

			case 'IFR_FINALIZED':
				if (activity.ifr) {
					components = {
						anchor: (
							<Link
								component={RouterLink}
								to={`/project/${activity.project?.idWbg}/ifr/${activity.ifr.id}`}
							/>
						),
					};
					i18nKey = 'activities.ifrFinalized';
					type = 'ifr';
					values = { name: activity.ifr.name };
				}
				break;

			case 'IFR_UPDATED':
				if (activity.ifr) {
					components = {
						anchor: (
							<Link
								component={RouterLink}
								to={`/project/${activity.project?.idWbg}/ifr/${activity.ifr.id}`}
							/>
						),
					};
					i18nKey = 'activities.ifrCreated';
					type = 'ifr';
					values = { name: activity.ifr.name };
				}
				break;

			case 'IFR_PLAN_CREATED':
				if (activity.ifrPlan) {
					components = {
						anchor: (
							<Link
								component={RouterLink}
								to={`/project/${activity.project?.idWbg}/plan/${activity.ifrPlan.id}`}
							/>
						),
					};
					i18nKey = 'activities.ifrPlanCreated';
					type = 'plan';
					values = { name: activity.ifrPlan.name };
				}
				break;

			case 'IFR_PLAN_UPDATED':
				if (activity.ifrPlan) {
					components = {
						anchor: (
							<Link
								component={RouterLink}
								to={`/project/${activity.project?.idWbg}/plan/${activity.ifrPlan.id}`}
							/>
						),
					};
					i18nKey = 'activities.ifrPlanUpdated';
					type = 'plan';
					values = { name: activity.ifrPlan.name };
				}
				break;

			case 'IFR_PLAN_VERSION_APPROVED':
				if (activity.ifrPlan) {
					components = {
						anchor: (
							<Link
								component={RouterLink}
								to={`/project/${activity.project?.idWbg}/plan/${activity.ifrPlan.id}`}
							/>
						),
					};
					i18nKey = 'activities.ifrPlanVersionApproved';
					type = 'plan';
					values = { name: activity.ifrPlan.name };
				}
				break;

			case 'IFR_PLAN_VERSION_CREATED':
				if (activity.ifrPlan) {
					components = {
						anchor: (
							<Link
								component={RouterLink}
								to={`/project/${activity.project?.idWbg}/plan/${activity.ifrPlan.id}`}
							/>
						),
					};
					i18nKey = 'activities.ifrPlanVersionCreated';
					type = 'plan';
					values = { name: activity.ifrPlan.name };
				}
				break;

			case 'IFR_PLAN_VERSION_FINALIZED':
				if (activity.ifrPlan) {
					components = {
						anchor: (
							<Link
								component={RouterLink}
								to={`/project/${activity.project?.idWbg}/plan/${activity.ifrPlan.id}`}
							/>
						),
					};
					i18nKey = 'activities.ifrPlanVersionFinalized';
					type = 'plan';
					values = { name: activity.ifrPlan.name };
				}
				break;

			case 'IFR_PLAN_VERSION_REJECTED':
				if (activity.ifrPlan) {
					components = {
						anchor: (
							<Link
								component={RouterLink}
								to={`/project/${activity.project?.idWbg}/plan/${activity.ifrPlan.id}`}
							/>
						),
					};
					i18nKey = 'activities.ifrPlanVersionRejected';
					type = 'plan';
					values = { name: activity.ifrPlan.name };
				}
				break;

			case 'IFR_PLAN_VERSION_UPDATED':
				if (activity.ifrPlan) {
					components = {
						anchor: (
							<Link
								component={RouterLink}
								to={`/project/${activity.project?.idWbg}/plan/${activity.ifrPlan.id}`}
							/>
						),
					};
					i18nKey = 'activities.ifrPlanVersionUpdated';
					type = 'plan';
					values = { name: activity.ifrPlan.name };
				}
				break;

			case 'LOAN_CREATED':
				i18nKey = 'activities.financingSourceCreated';
				if (activity.loan) {
					components = {
						anchor: <strong />,
					};
					type = financierIconTypes[activity.loan.financier];
					values = { id: activity.loan.code };
				}
				break;

			case 'LOAN_ENABLED':
				i18nKey = 'activities.loanEnabled';
				if (activity.loan) {
					components = {
						anchor: <strong />,
					};
					type = financierIconTypes[activity.loan.financier];
					values = { id: activity.loan.code };
				}
				break;

			case 'LOAN_UPDATED':
				i18nKey = 'activities.loanUpdated';
				if (activity.loan) {
					components = {
						anchor: <strong />,
					};
					type = financierIconTypes[activity.loan.financier];
					values = { id: activity.loan.code };
				}
				break;

			case 'PROGRAM_CREATED':
				if (activity.program) {
					components = {
						anchor: (
							<Link
								component={RouterLink}
								to={`/project/${activity.project?.idWbg}/settings/program`}
							/>
						),
					};
					i18nKey = 'activities.programCreated';
					type = 'project';
					values = { name: activity.program.name };
				}
				break;

			case 'PROGRAM_UPDATED':
				if (activity.program) {
					components = {
						anchor: (
							<Link
								component={RouterLink}
								to={`/project/${activity.project?.idWbg}/settings/program`}
							/>
						),
					};
					i18nKey = 'activities.programUpdated';
					type = 'project';
					values = { name: activity.program.name };
				}
				break;

			case 'PROJECT_CREATED':
				if (activity.project) {
					components = {
						anchor: (
							<Link
								component={RouterLink}
								fontWeight={600}
								to={`/project/${activity.project?.idWbg}`}
								underline="hover"
							/>
						),
					};
					i18nKey = 'activities.projectCreated';
					type = 'project';
					values = { id: activity.project?.idWbg };
				}
				break;

			case 'PROJECT_UPDATED':
				if (activity.project) {
					components = {
						anchor: (
							<Link
								component={RouterLink}
								fontWeight={600}
								to={`/project/${activity.project?.idWbg}`}
								underline="hover"
							/>
						),
					};
					i18nKey = 'activities.projectUpdated';
					type = 'project';
					values = { id: activity.project?.idWbg };
				}
				break;

			case 'RECONCILIATION_ITEM_ACTIVATED':
				if (activity.reconciliationItem) {
					if (activity.project) {
						components = {
							anchor: (
								<Link
									component={RouterLink}
									to={`/project/${activity.project?.idWbg}/settings/reconciliation-items`}
								/>
							),
						};
						i18nKey = 'activities.reconciliationItemActivated';
					} else {
						components = {
							strong: <strong />,
						};
						i18nKey = 'activities.reconciliationItemActivated';
					}
					type = 'reconciliationItem';
					values = { name: activity.reconciliationItem.name };
				}
				break;

			case 'RECONCILIATION_ITEM_CREATED':
				if (activity.reconciliationItem) {
					if (activity.project) {
						components = {
							anchor: (
								<Link
									component={RouterLink}
									to={`/project/${activity.project?.idWbg}/settings/reconciliation-items`}
								/>
							),
						};
						i18nKey = 'activities.reconciliationItemCreated';
					} else {
						components = {
							strong: <strong />,
						};
						i18nKey = 'activities.reconciliationItemCreated';
					}
					type = 'reconciliationItem';
					values = { name: activity.reconciliationItem.name };
				}
				break;

			case 'RECONCILIATION_ITEM_DEACTIVATED':
				if (activity.reconciliationItem) {
					if (activity.project) {
						components = {
							anchor: (
								<Link
									component={RouterLink}
									to={`/project/${activity.project?.idWbg}/settings/reconciliation-items`}
								/>
							),
						};
						i18nKey = 'activities.reconciliationItemDeactivated';
					} else {
						components = {
							strong: <strong />,
						};
						i18nKey = 'activities.reconciliationItemDeactivated';
					}
					type = 'reconciliationItem';
					values = { name: activity.reconciliationItem.name };
				}
				break;

			case 'RECONCILIATION_ITEM_UPDATED':
				if (activity.reconciliationItem) {
					if (activity.project) {
						components = {
							anchor: (
								<Link
									component={RouterLink}
									to={`/project/${activity.project?.idWbg}/settings/reconciliation-items`}
								/>
							),
						};
						i18nKey = 'activities.reconciliationItemUpdated';
					} else {
						components = {
							strong: <strong />,
						};
						i18nKey = 'activities.reconciliationItemUpdated';
					}
					type = 'reconciliationItem';
					values = { name: activity.reconciliationItem.name };
				}
				break;

			case 'TRANSACTION_CREATED':
				if (activity.transaction) {
					type = transactionIconTypes[activity.transaction.type];
					values = { amount, fromAccount, fromEntity, toAccount };

					let tab = `payments`;
					let to;

					switch (activity.transaction.type) {
						case 'ADVANCE':
							i18nKey = 'activities.transactionAdvanceCreated';
							break;
						case 'CASH_BENEFIT':
							i18nKey = 'activities.transactionCashBenefitCreated';
							tab = 'cash-benefits';
							break;
						case 'DIRECT':
							i18nKey = 'activities.transactionDirectPaymentCreated';
							break;
						case 'INITIAL':
							i18nKey = 'activities.transactionInitialCreated';
							break;
						case 'NON_CASH_BENEFIT':
							i18nKey = 'activities.transactionNonCashBenefitCreated';
							tab = 'non-cash-benefits';
							break;
						case 'OPEX':
							i18nKey = 'activities.transactionOpExCreated';
							tab = 'opex';
							break;
						case 'PAYMENT':
							i18nKey = 'activities.transactionPaymentCreated';
							break;
						case 'PAYMENT_REFUND':
							i18nKey = 'activities.transactionPaymentRefundCreated';
							tab = 'payment-refunds';
							break;
						case 'REIMBURSEMENT':
							i18nKey = 'activities.transactionReimbursementCreated';
							to = `/project/${activity.project?.idWbg}/account/${toAccount?.id}`;
							break;
						case 'REPLENISHMENT':
							i18nKey = 'activities.transactionReplenishmentCreated';
							to = `/project/${activity.project?.idWbg}/account/${toAccount?.id}`;
							break;
						case 'SPECIAL':
							i18nKey = 'activities.transactionSpecialCreated';
							to = `/project/${activity.project?.idWbg}/account/${toAccount?.id}`;
							break;
						case 'TRANSFER':
							i18nKey = 'activities.transactionTransferCreated';
							tab = 'transfers';
							break;
					}

					components = {
						anchor: (
							<Link
								component={RouterLink}
								fontWeight={600}
								to={to || `/project/${activity.project?.idWbg}/transactions/${tab}`}
								underline="hover"
							/>
						),
						anchorFromAccount: <LinkAccount account={fromAccount as Account} />,
						anchorFromEntity: <LinkEntity entity={activity.transaction.fromEntity as Entity} />,
						anchorToAccount: <LinkAccount account={toAccount as Account} />,
					};
				}
				break;

			case 'TRANSACTION_CONFIRMED':
				if (activity.transaction) {
					const isReceived = activity.transaction.isReceived;

					type = transactionIconTypes[activity.transaction.type];
					values = { amount, toEntity };

					let tab = `payments`;

					switch (activity.transaction.type) {
						case 'CASH_BENEFIT':
							i18nKey = isReceived
								? 'activities.transactionCashBenefitReceived'
								: 'activities.transactionCashBenefitNotReceived';
							tab = 'cash-benefits';
							break;
						case 'PAYMENT':
							i18nKey = isReceived
								? 'activities.transactionPaymentReceived'
								: 'activities.transactionPaymentNotReceived';
							break;
					}

					components = {
						anchor: (
							<Link
								component={RouterLink}
								fontWeight={600}
								to={`/project/${activity.project?.idWbg}/transactions/${tab}`}
								underline="hover"
							/>
						),
						anchorToEntity: <LinkEntity entity={toEntity} />,
					};
				}
				break;

			case 'TRANSACTION_REVERTED':
				if (activity.transaction) {
					type = transactionIconTypes[activity.transaction.type];
					values = { amount, fromAccount, fromEntity, toAccount };

					let tab = `payments`;
					let to;

					switch (activity.transaction.type) {
						case 'ADVANCE':
							i18nKey = 'activities.transactionAdvanceReverted';
							break;
						case 'CASH_BENEFIT':
							i18nKey = 'activities.transactionCashBenefitReverted';
							tab = 'cash-benefits';
							break;
						case 'DIRECT':
							i18nKey = 'activities.transactionDirectPaymentReverted';
							break;
						case 'INITIAL':
							i18nKey = 'activities.transactionInitialReverted';
							break;
						case 'NON_CASH_BENEFIT':
							i18nKey = 'activities.transactionNonCashBenefitReverted';
							tab = 'non-cash-benefits';
							break;
						case 'OPEX':
							i18nKey = 'activities.transactionOpExReverted';
							tab = 'opex';
							break;
						case 'PAYMENT':
							i18nKey = 'activities.transactionPaymentReverted';
							break;
						case 'REIMBURSEMENT':
							i18nKey = 'activities.transactionReimbursementReverted';
							to = `/project/${activity.project?.idWbg}/account/${toAccount?.id}`;
							break;
						case 'REPLENISHMENT':
							i18nKey = 'activities.transactionReplenishmentReverted';
							to = `/project/${activity.project?.idWbg}/account/${toAccount?.id}`;
							break;
						case 'SPECIAL':
							i18nKey = 'activities.transactionSpecialReverted';
							to = `/project/${activity.project?.idWbg}/account/${toAccount?.id}`;
							break;
						case 'TRANSFER':
							i18nKey = 'activities.transactionTransferReverted';
							tab = 'transfers';
							break;
					}
					components = {
						anchor: (
							<Link
								component={RouterLink}
								fontWeight={600}
								to={to || `/project/${activity.project?.idWbg}/transactions/${tab}`}
								underline="hover"
							/>
						),
						anchorFromAccount: <LinkAccount account={fromAccount as Account} />,
						anchorFromEntity: <LinkEntity entity={activity.transaction.fromEntity as Entity} />,
						anchorToAccount: <LinkAccount account={toAccount as Account} />,
					};
				}
				break;

			case 'TRANSACTION_UPDATED':
				if (activity.transaction) {
					type = transactionIconTypes[activity.transaction.type];
					values = { amount, fromAccount, fromEntity, toAccount };

					let tab = `payments`;
					let to;

					switch (activity.transaction.type) {
						case 'ADVANCE':
							i18nKey = 'activities.transactionAdvanceUpdated';
							break;
						case 'CASH_BENEFIT':
							i18nKey = 'activities.transactionCashBenefitUpdated';
							tab = 'cash-benefits';
							break;
						case 'DIRECT':
							i18nKey = 'activities.transactionDirectPaymentUpdated';
							break;
						case 'INITIAL':
							i18nKey = 'activities.transactionInitialUpdated';
							break;
						case 'NON_CASH_BENEFIT':
							i18nKey = 'activities.transactionNonCashBenefitUpdated';
							tab = 'non-cash-benefits';
							break;
						case 'OPEX':
							i18nKey = 'activities.transactionOpExUpdated';
							tab = 'opex';
							break;
						case 'PAYMENT':
							i18nKey = 'activities.transactionPaymentUpdated';
							break;
						case 'PAYMENT_REFUND':
							i18nKey = 'activities.transactionPaymentRefundUpdated';
							tab = 'payment-refunds';
							break;
						case 'REIMBURSEMENT':
							i18nKey = 'activities.transactionReimbursementUpdated';
							to = `/project/${activity.project?.idWbg}/transactions/reimbursements`;
							break;
						case 'REPLENISHMENT':
							i18nKey = 'activities.transactionReplenishmentUpdated';
							to = `/project/${activity.project?.idWbg}/account/${toAccount?.id}`;
							break;
						case 'SPECIAL':
							i18nKey = 'activities.transactionSpecialUpdated';
							to = `/project/${activity.project?.idWbg}/account/${toAccount?.id}`;
							break;
						case 'TRANSFER':
							i18nKey = 'activities.transactionTransferUpdated';
							tab = 'transfers';
							break;
					}
					components = {
						anchor: (
							<Link
								component={RouterLink}
								fontWeight={600}
								to={to || `/project/${activity.project?.idWbg}/transactions/${tab}`}
								underline="hover"
							/>
						),
						anchorFromAccount: <LinkAccount account={fromAccount as Account} />,
						anchorFromEntity: <LinkEntity entity={activity.transaction.fromEntity as Entity} />,
						anchorToAccount: <LinkAccount account={toAccount as Account} />,
					};
				}
				break;

			case 'ILAP':
				if (activity.ilapJob) {
					const { category, message, messageType, result, status } = activity.ilapJob;
					const messageTypeLower = messageType?.toLowerCase();
					const valuesIlap: Record<string, unknown> = {};

					if (message) {
						if (category === 'ILAP_NO_PROJECT_FOUND') {
							valuesIlap.projectId = message?.Project?.ID;
						} else if (category === 'ILAP_NO_DA_FOUND' || messageTypeLower === 'da') {
							valuesIlap.daName = message?.DA?.NAME;
						} else if (category === 'ILAP_NO_BANK_FOUND') {
							valuesIlap.bankName = message?.BeneficiaryBankDetails?.NAME;
						} else if (
							category === 'ILAP_NO_SUPPLIER_FOUND' ||
							messageTypeLower === 'direct' ||
							messageTypeLower === 'reimb'
						) {
							valuesIlap.supplierName = message?.BeneficiaryDetails?.NAME;
						}
					}

					if (messageTypeLower) {
						if (status === 'COMPLETED') {
							title = t(`activities.ilap.${messageTypeLower}.completed.title`);
							i18nKey = `activities.ilap.${messageTypeLower}.completed.description`;
						} else if (category) {
							const categoryKey = toCamelCase(category.replace('ILAP_', ''));
							title = t(`activities.ilap.${messageTypeLower}.failed.title`);
							i18nKey = `activities.ilap.${messageTypeLower}.failed.${categoryKey}.description`;
						} else {
							i18nKey = result?.errors?.[1] || 'activities.ilapJobFailed';
						}
					} else {
						i18nKey = 'activities.ilap.unknown.description';
						title = t('activities.ilap.unknown.title');
					}

					values = valuesIlap;

					// Set icon type based on category
					type = category ? ilapCategoryIconTypes[category] : 'ilap';

					components = { strong: <strong /> };
				}
				break;
			case 'USER_ADDED':
				if (activity.user && activity.entity) {
					components = { anchor: linkEntity };
					i18nKey = 'activities.userAdded';
					type = 'user';
					values = {
						entityName: activity.entity.name,
						name: `${activity.user.firstName} ${activity.user.lastName}`,
					};
				}
				break;

			case 'USER_CREATED':
				if (activity.user) {
					components = { strong: <strong /> };
					i18nKey = 'activities.userCreated';
					type = 'user';
					values = { name: `${activity.user.firstName} ${activity.user.lastName}` };
				}
				break;

			case 'USER_REMOVED':
				if (activity.user && activity.entity) {
					components = { anchor: linkEntity };
					i18nKey = 'activities.userRemoved';
					type = 'user';
					values = {
						entityName: activity.entity.name,
						name: `${activity.user.firstName} ${activity.user.lastName}`,
					};
				}
				break;

			case 'USER_UPDATED':
				if (activity.user) {
					components = { strong: <strong /> };
					i18nKey = 'activities.userUpdated';
					type = 'user';
					values = { name: `${activity.user.firstName} ${activity.user.lastName}` };
				}
				break;
		}

		// V2 audit-log fallback: most v2 lifecycle activity types
		// (VOUCHER_*, CONTRACT_*, WORK_PLAN_*, SPEND_PLAN_*, INVOICE_*,
		// PLANNING_ACTIVITY_*, CONDITION_*, EXCEPTION_*) write activity rows
		// stamped only with FK columns (voucher_id, contract_id, ...) and
		// don't have a populated entity object on the activity. Map their
		// type directly to an `activities.v2*` translation key so the audit
		// log renders the right sentence instead of "An unknown activity
		// occured."
		if (!i18nKey && activity.type) {
			const v2Key = activityTypeToV2I18nKey[activity.type];
			if (v2Key) {
				i18nKey = v2Key;
			}
		}

		// If to is undefined, try extracting `to` from the anchor component
		if (
			!to &&
			components?.anchor &&
			typeof components.anchor === 'object' &&
			'props' in components.anchor
		) {
			to = components.anchor.props.to;
		}

		return {
			activity,
			content: (
				<>
					<Typography>
						<Trans
							components={components}
							i18nKey={i18nKey || 'activities.unknown'}
							values={values}
						/>
					</Typography>
					{/* V2: Show activity note (e.g. rejection reason, hold reason) */}
					{activity.note && (
						<Typography
							variant="body2"
							sx={{
								mt: 0.75,
								pl: 1.5,
								borderLeft: '3px solid',
								borderColor: 'warning.main',
								color: 'text.secondary',
								fontStyle: 'italic',
							}}>
							{activity.note}
						</Typography>
					)}
				</>
			),
			date: new Date(activity.created),
			title: title || translateActivityType(activity.type, t),
			to,
			type,
		};
	});
};
